import api from './index';

// 抗体分析接口类型定义
export interface AntibodyCDRRequest {
  sequence: string;
  name?: string;
  scheme?: string;
  cdr_definition?: string;
}

export interface EpitopeRequest {
  sequence: string;
  name?: string;
  prediction_mode?: string;
  threshold?: number;
  top_candidates?: number;
  rolling_window_size?: number;
  use_rolling_mean?: boolean;
}

export interface EpitopeResponse {
  success: boolean;
  message: string;
  job_id?: string;
  raw_predictions?: Array<{
    Accession: string;
    Residue: string;
    'BepiPred-3.0 score': number;
    'BepiPred-3.0 rolling mean score': number;
  }>;
  top_epitopes?: Array<{
    header: string;
    sequence: string;
    length: number;
  }>;
  summary_stats?: {
    total_residues: number;
    mean_score: number;
    max_score: number;
    min_score: number;
    high_confidence_residues: number;
  };
  visualization_data?: {
    html_content?: string;
  };
  error?: string;
}

export interface BatchAntibodyCDRRequest {
  sequences: {
    sequence: string;
    name?: string;
  }[];
  scheme?: string;
  cdr_definition?: string;
}

// 抗体分析任务类型定义
export interface AntibodyAnalysisTask {
  module_id: string;
  module_name: string;
  module_description: string;
  endpoint: string;
  method: string[];
  name: string;
  description: string;
}

// 抗体分析服务
// 添加结构预测接口类型定义
export interface AntibodyStructureRequest {
  heavy_chain: string;
  light_chain: string;
  name?: string;
}


export interface StructurePredictionResponse {
  success: boolean;
  message: string;
  pdb_data?: string;
  model_confidence?: number;
  job_id?: string;
  error?: string;
}

// 新的批量/FASTA 输入与响应类型
export interface BatchCDRInput {
  fasta_text?: string; // 可传入FASTA文本，或单条非FASTA序列
  sequences?: { sequence: string; name?: string }[]; // 或者传列表
  scheme?: string;
  cdr_definition?: string;
}

export interface NumberedPosition {
  position: string;
  aa: string;
  region: string;
}

export interface CDRAnalysisResult {
  success: boolean;
  sequence: string;
  name?: string;
  scheme: string;
  cdr_definition: string;
  chain_type: string;
  regions: Record<string, string>;
  numbered_positions?: NumberedPosition[];
  formatted_sequence?: string;
  species?: string;
  v_gene?: string;
  j_gene?: string;
  error?: string;
}

export interface BatchCDRAnalysisResult {
  success: boolean;
  count: number;
  results: Record<string, CDRAnalysisResult>;
  error?: string;
  task_id?: string;  // 添加任务ID字段（用于任务管理器模式）
}

// 在antibodyService对象中添加新方法
export const antibodyService = {
  // 获取所有可用的抗体分析任务
  getAnalysisTasks: async (): Promise<AntibodyAnalysisTask[]> => {
    try {
      return await api.get('/antibody_analysis/tasks');
    } catch (error) {
      console.error('获取抗体分析任务失败:', error);
      return [];
    }
  },
  
  // 执行指定的抗体分析任务（通过任务管理器）
  executeAnalysisTask: async (taskType: string, handlerName: string, params: any) => {
    try {
      // 导入任务管理器客户端
      const { taskManagerClient } = await import('../utils/taskManager');
      
      // 构建标准的任务配置
      const taskConfig = {
        name: `抗体分析 - ${params.name || 'Analysis'}`,
        description: `执行抗体分析任务`,
        service_name: 'antibody_service',
        task_type: taskType,
        handler_name: handlerName,
        parameters: params,
        priority: 5,
        timeout: 3600
      };
      
      console.log('通过任务管理器提交抗体分析任务:', taskConfig);
      const taskResponse = await taskManagerClient.createTask(taskConfig);
      console.log('抗体分析任务已创建:', taskResponse.id);
      
      return {
        success: true,
        task_id: taskResponse.id,
        message: '任务已提交到任务管理器'
      };
    } catch (error) {
      console.error(`抗体分析任务提交失败:`, error);
      throw error;
    }
  },

  // 批量执行抗体分析任务
  executeBatchTask: async (endpoint: string, batchParams: BatchAntibodyCDRRequest) => {
    try {
      // 创建队列保存所有请求的Promise
      const requestPromises = batchParams.sequences.map(seqData => {
        const params = {
          sequence: seqData.sequence,
          name: seqData.name,
          scheme: batchParams.scheme || 'kabat',
          cdr_definition: batchParams.cdr_definition || batchParams.scheme || 'kabat'
        };
        
        return api.post(`/antibody_analysis${endpoint}`, params);
      });
      
      // 并行执行所有请求
      const results = await Promise.all(requestPromises);
      console.log(`批量任务 ${endpoint} 执行完成，共处理 ${results.length} 个序列`);
      
      return results;
    } catch (error) {
      console.error(`批量任务 ${endpoint} 执行失败:`, error);
      throw error;
    }
  },

  // 抗体CDR区预测
  predictCDR: async (data: AntibodyCDRRequest) => {
    try {
      console.log('发送CDR预测请求:', {
        sequence: data.sequence,
        scheme: data.scheme || 'chothia',
        cdr_definition: data.cdr_definition || data.scheme || 'chothia'
      });
      
      // 由于api拦截器已经处理了response.data，这里直接返回response
      const response = await api.post('/antibody_analysis/findcdr/cdr_analyze', {
        sequence: data.sequence,
        scheme: data.scheme || 'chothia',
        cdr_definition: data.cdr_definition || data.scheme || 'chothia'
      });
      
      // 打印完整的响应
      console.log('CDR预测API响应:', response);
      
      // 直接返回响应，不再访问.data属性
      return response;
    } catch (error) {
      console.error('CDR预测请求失败:', error);
      throw error;
    }
  },
  
  // 批量抗体CDR区预测 - 使用统一任务API
  batchPredictCDR: async (data: BatchCDRInput): Promise<BatchCDRAnalysisResult> => {
    try {
      console.log('发送批量CDR预测请求:', data);
      // 使用统一任务API，传入sequence参数支持FASTA格式
      const params = {
        sequence: data.fasta_text,  // 改为sequence参数
        scheme: data.scheme,
        cdr_definition: data.cdr_definition
      };
      const response = await antibodyService.executeAnalysisTask('antibody_cdr', 'cdr_analyze_json', params);
      console.log('批量CDR预测API响应:', response);
      
      // 返回兼容的格式
      return {
        success: response.success,
        count: 0,  // 任务提交阶段无法知道具体数量
        results: {},  // 任务提交阶段无结果
        task_id: response.task_id
      };
    } catch (error) {
      console.error('批量CDR预测失败:', error);
      throw error;
    }
  },
  
  // 表位预测
    // 表位预测
  predictEpitope: async (data: EpitopeRequest): Promise<EpitopeResponse> => {
    try {
      return await api.post('/antibody_analysis/epitope_prediction/predict', data);
    } catch (error) {
      console.error('表位预测失败:', error);
      throw error;
    }
  },
  
  getEpitopeResult: async (jobId: string): Promise<EpitopeResponse> => {
    try {
      return await api.get(`/antibody_analysis/epitope_prediction/result/${jobId}`);
    } catch (error) {
      console.error('获取表位预测结果失败:', error);
      throw error;
    }
  }
};

export default antibodyService;